import React, { useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import LockIcon from "@mui/icons-material/Lock";
import { updateUserAccount } from "service/api";
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  Container,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CountryAutocomplete from "components/country-autocomplete";
import { motion } from "framer-motion";
import { LoadingButton } from "@mui/lab";
import { MuiTelInput, MuiTelInputCountry } from "mui-tel-input";
import BrandingLogo from "components/branding-logo";
import { useAppDispatch } from "store";
import { differenceInYears } from "date-fns";
import { errorNotification } from "../../features/notifications/notificationsSlice";
import { useAccountQuery } from "../../features/auth/authApi";
import { parsePhoneNumberFromString } from "libphonenumber-js";

type InitialValues = {
  first_name: string;
  last_name: string;
  country: string;
  citizenship_country: string;
  user_birth_date: string;
  gender: string;
  city: string;
  state: string;
  zip_code: string;
  address: string;
  isDirector: boolean;
  isUBO: boolean;
  isAuthRep: boolean;
};

const SignUpWithReferral = () => {
  const { data: account } = useAccountQuery();

  const dispatch = useAppDispatch();

  const onSubmit = async (values: InitialValues) => {
    const { isDirector, isUBO, isAuthRep, ...payload } = values;

    try {
      await updateUserAccount(payload);
    } catch (err: any) {
      dispatch(errorNotification(err.message || "Update failed"));
    } finally {
      window.location.reload();
    }
  };

  const maxAge = account?.user?.kycProgram?.max_age_in_registration;

  const resolvedMaxAge = maxAge ?? 99;

  const initialValues: InitialValues = {
    first_name: "",
    last_name: "",
    user_birth_date: "",
    country: "",
    citizenship_country: "",
    gender: "",
    city: "",
    state: "",
    zip_code: "",
    address: "",
    isDirector: false,
    isUBO: false,
    isAuthRep: false,
  };

  const validationSchema = Yup.object().shape({
    first_name: Yup.string().required("Applicant First Name is required"),
    last_name: Yup.string().required("Applicant Last Name is required"),
    country: Yup.string().required("Residence country is required"),
    user_birth_date: Yup.string()
      .required("Birth date is required")
      .test(
        "user_birth_date",
        `your age must be between 18-${resolvedMaxAge} (inclusive)`,
        (value) => {
          if (value) {
            const diff = differenceInYears(new Date(), new Date(value));
            return diff >= 18 && diff <= resolvedMaxAge;
          }
          return false;
        }
      ),
    gender: Yup.string().required("Gender is required"),

    citizenship_country: Yup.string().required(
      "Citizenship country is required"
    ),
    city: Yup.string().required("City is required"),
    address: Yup.string().required("Address is required"),
    zip_code: Yup.string().required("Zip code is required"),
    isDirector: Yup.boolean(),
    isUBO: Yup.boolean(),
  });

  function getCountryFromPhone(phone?: string): MuiTelInputCountry {
    if (!phone) return "CZ";
    const parsed = parsePhoneNumberFromString(phone);
    return (parsed?.country as MuiTelInputCountry) || "CZ";
  }

  const {
    handleSubmit,
    getFieldMeta,
    setFieldValue,
    handleChange,
    handleBlur,
    isSubmitting,
    values,
  } = useFormik<InitialValues>({
    initialValues,
    onSubmit,
    validationSchema,
  });

  const selectedCount = [
    values.isDirector,
    values.isUBO,
    values.isAuthRep,
  ].filter(Boolean).length;

  useEffect(() => {
    localStorage.removeItem("managementStructure");
    localStorage.removeItem("ownershipStructure");
  }, [setFieldValue]);

  const handleGenderChange = (event: { target: { value: string } }) => {
    setFieldValue("gender", event.target.value);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const personal = {
      fullName: `${values.first_name} ${values.last_name}`,
      email: account?.user.login,
      phone: account?.user.phone,
      country: values.country,
      address: values.address,
      zip: values.zip_code,
      city: values.city,
      state: values.state,
      nationality: values.citizenship_country,
      date_of_birth: values.user_birth_date,
    };

    if (values.isDirector) {
      localStorage.setItem("managementStructure", JSON.stringify([personal]));
    }
    if (values.isUBO) {
      localStorage.setItem(
        "ownershipStructure",
        JSON.stringify([{ ...personal, ownershipPercentage: 100 }])
      );
    }
  }, [
    values.isDirector,
    values.isUBO,
    values.first_name,
    values.last_name,
    values.country,
    values.address,
    values.zip_code,
    values.city,
    values.state,
    values.citizenship_country,
    values.user_birth_date,
    account?.user.login,
    account?.user.phone,
  ]);

  return (
    <Box
      display={"flex"}
      alignItems="center"
      justifyContent="center"
      sx={{ mt: 4 }}
    >
      <Container maxWidth="sm">
        <form onSubmit={handleSubmit}>
          <Card elevation={24} component={motion.div} layoutId="auth-card">
            <Box paddingY={2.5} paddingX={4} sx={{ bgcolor: "brand.auth" }}>
              <BrandingLogo width={200} />
            </Box>
            <CardContent>
              <Stack spacing={2}>
                <Box display="flex" flexDirection="row" alignItems="center">
                  <LockIcon fontSize="small" />
                  <Typography variant="h6" fontWeight={"600"}>
                    Signup Form
                  </Typography>
                </Box>
                <Grid container rowSpacing={2} columnSpacing={0.5}>
                  <Grid xs={12} item>
                    <TextField
                      label="Applicant First Name"
                      name="first_name"
                      variant="outlined"
                      value={getFieldMeta("first_name").value}
                      fullWidth
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        getFieldMeta("first_name").touched &&
                        !!getFieldMeta("first_name").error
                      }
                      helperText={
                        getFieldMeta("first_name").touched &&
                        getFieldMeta("first_name").error
                      }
                    />
                  </Grid>
                  <Grid xs={12} item>
                    <TextField
                      label="Applicant Last Name"
                      name="last_name"
                      variant="outlined"
                      value={getFieldMeta("last_name").value}
                      fullWidth
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        getFieldMeta("last_name").touched &&
                        !!getFieldMeta("last_name").error
                      }
                      helperText={
                        getFieldMeta("last_name").touched &&
                        getFieldMeta("last_name").error
                      }
                    />
                  </Grid>
                  {account?.user.wallet.wallet_type === "Business Wallet" && (
                    <Grid item xs={12}>
                      <FormControl component="fieldset" fullWidth>
                        <FormLabel component="legend">
                          Please select your position within the company?
                        </FormLabel>
                        <FormGroup row>
                          <FormControlLabel
                            control={
                              <Checkbox
                                name="isDirector"
                                checked={values.isDirector}
                                onChange={handleChange}
                                disabled={
                                  selectedCount >= 2 && !values.isDirector
                                }
                              />
                            }
                            label="Director"
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                name="isUBO"
                                checked={values.isUBO}
                                onChange={handleChange}
                                disabled={selectedCount >= 2 && !values.isUBO}
                              />
                            }
                            label="Ultimate Beneficial Owner"
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                name="isAuthRep"
                                checked={values.isAuthRep}
                                onChange={handleChange}
                                disabled={
                                  selectedCount >= 2 && !values.isAuthRep
                                }
                              />
                            }
                            label="Authorized Representative"
                          />
                        </FormGroup>
                      </FormControl>
                    </Grid>
                  )}
                  <Grid xs={12} item>
                    <TextField
                      type="date"
                      label="Date Of Birth"
                      name="user_birth_date"
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      value={getFieldMeta("user_birth_date").value}
                      fullWidth
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        getFieldMeta("user_birth_date").touched &&
                        !!getFieldMeta("user_birth_date").error
                      }
                      helperText={
                        getFieldMeta("user_birth_date").touched &&
                        getFieldMeta("user_birth_date").error
                      }
                    />
                  </Grid>
                  <Grid xs={12} item>
                    <FormControl>
                      <FormLabel id="demo-radio-buttons-group-label">
                        Gender
                      </FormLabel>
                      <RadioGroup name="gender" onChange={handleGenderChange}>
                        <Box display="flex" flexDirection="row">
                          <FormControlLabel
                            value="0"
                            control={<Radio />}
                            label="Female"
                          />
                          <FormControlLabel
                            value="1"
                            control={<Radio />}
                            label="Male"
                          />
                        </Box>
                      </RadioGroup>
                      {getFieldMeta("gender").touched &&
                        getFieldMeta("gender").error && (
                          <React.Fragment>
                            <Typography
                              component="span"
                              sx={{ color: "error.main" }}
                            >
                              {getFieldMeta("gender").error}
                            </Typography>
                          </React.Fragment>
                        )}
                    </FormControl>
                  </Grid>
                  <Grid xs={12} item>
                    <TextField
                      label="Email"
                      name="email"
                      sx={{ flexShrink: 1 }}
                      variant="outlined"
                      disabled={true}
                      value={account?.user.login}
                      fullWidth
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        getFieldMeta("email").touched &&
                        !!getFieldMeta("email").error
                      }
                      helperText={
                        getFieldMeta("email").touched &&
                        getFieldMeta("email").error
                      }
                    />
                  </Grid>
                  <Grid xs={12} item>
                    <MuiTelInput
                      excludedCountries={["IR"]}
                      focusOnSelectCountry
                      forceCallingCode
                      fullWidth
                      defaultCountry={getCountryFromPhone(account?.user?.phone)}
                      label="Mobile"
                      name="phone"
                      disabled={true}
                      value={account?.user.phone}
                      error={
                        getFieldMeta("phone").touched &&
                        !!getFieldMeta("phone").error
                      }
                      helperText={
                        getFieldMeta("phone").touched &&
                        getFieldMeta("phone").error
                      }
                    />
                    <React.Fragment>
                      <Typography variant="body2" color="textSecondary">
                        Please select your country flag, then enter your phone
                        number without the country code.
                      </Typography>
                    </React.Fragment>
                  </Grid>

                  <Grid xs={12} item>
                    <CountryAutocomplete
                      label="Residence Country"
                      onBlur={handleBlur}
                      name="country"
                      value={getFieldMeta("country").value}
                      onChange={handleChange}
                      kycProgramId={account?.user?.wallet?.kyc_program_id}
                      error={
                        getFieldMeta("country").touched
                          ? getFieldMeta("country").error
                          : undefined
                      }
                    />
                  </Grid>
                  <Grid xs={12} item>
                    <CountryAutocomplete
                      label="Citizenship Country"
                      onBlur={handleBlur}
                      name="citizenship_country"
                      value={getFieldMeta("citizenship_country").value}
                      onChange={handleChange}
                      kycProgramId={account?.user?.wallet?.kyc_program_id}
                      error={
                        getFieldMeta("citizenship_country").touched
                          ? getFieldMeta("citizenship_country").error
                          : undefined
                      }
                    />
                  </Grid>
                  <Grid xs={12} item>
                    <TextField
                      label="State"
                      name="state"
                      sx={{ flexShrink: 1 }}
                      variant="outlined"
                      value={getFieldMeta("state").value}
                      fullWidth
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        getFieldMeta("state").touched &&
                        !!getFieldMeta("state").error
                      }
                      helperText={
                        getFieldMeta("state").touched &&
                        getFieldMeta("state").error
                      }
                    />
                  </Grid>
                  <Grid xs={12} item>
                    <TextField
                      label="City"
                      name="city"
                      sx={{ flexShrink: 1 }}
                      variant="outlined"
                      value={getFieldMeta("city").value}
                      fullWidth
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        getFieldMeta("city").touched &&
                        !!getFieldMeta("city").error
                      }
                      helperText={
                        getFieldMeta("city").touched &&
                        getFieldMeta("city").error
                      }
                    />
                  </Grid>
                  <Grid xs={12} item>
                    <TextField
                      label="Address"
                      name="address"
                      sx={{ flexShrink: 1 }}
                      variant="outlined"
                      value={getFieldMeta("address").value}
                      fullWidth
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        getFieldMeta("address").touched &&
                        !!getFieldMeta("address").error
                      }
                      helperText={
                        getFieldMeta("address").touched &&
                        getFieldMeta("address").error
                      }
                    />
                  </Grid>
                  <Grid xs={12} item>
                    <TextField
                      label="Zip Code"
                      name="zip_code"
                      sx={{ flexShrink: 1 }}
                      variant="outlined"
                      value={getFieldMeta("zip_code").value}
                      fullWidth
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        getFieldMeta("zip_code").touched &&
                        !!getFieldMeta("zip_code").error
                      }
                      helperText={
                        getFieldMeta("zip_code").touched &&
                        getFieldMeta("zip_code").error
                      }
                    />
                  </Grid>
                </Grid>
                <Stack spacing={2}>
                  <Box
                    display="flex"
                    flexDirection="column"
                    gap={2}
                    alignItems="center"
                  >
                    <LoadingButton
                      fullWidth
                      loading={isSubmitting}
                      variant="contained"
                      type="submit"
                    >
                      Submit
                    </LoadingButton>
                  </Box>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </form>
      </Container>
    </Box>
  );
};
export default SignUpWithReferral;
